export type HospitalCountry = 'FR' | 'US';

export type PractitionerNameSource = {
  name: string;
  medicalCredentials?: string | null;
};

type PractitionerNameParts = {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  medicalCredentials?: string | null;
};

export const getPractitionerFullName = (
  practitioner: PractitionerNameParts
): string =>
  [practitioner.firstName, practitioner.middleName, practitioner.lastName]
    .filter(Boolean)
    .join(' ');

/** Matches both full identity names and the first/last names saved by the picker. */
export const getPractitionerNameSources = (
  practitioners: readonly PractitionerNameParts[]
): PractitionerNameSource[] =>
  practitioners.flatMap((practitioner) =>
    Array.from(
      new Set([
        getPractitionerFullName(practitioner),
        `${practitioner.firstName} ${practitioner.lastName}`
      ])
    ).map((name) => ({
      name,
      medicalCredentials: practitioner.medicalCredentials
    }))
  );

const LEADING_DOCTOR_TITLE = /^\s*dr\.?(?=\s|$)\s*/i;
const nameWithoutDoctorTitle = (name: string): string =>
  name.replace(LEADING_DOCTOR_TITLE, '').trim();

const comparableName = (name: string): string =>
  nameWithoutDoctorTitle(name).replace(/\s+/g, ' ').toLowerCase();

export function formatPractitionerName(
  name: string,
  country: HospitalCountry | null | undefined,
  medicalCredentials?: string | null,
  assumeDoctor = false
): string {
  if (!country || (medicalCredentials !== 'MD' && !assumeDoctor)) {
    return name;
  }

  const title = country === 'US' ? 'Dr.' : 'Dr';
  const nameWithoutTitle = nameWithoutDoctorTitle(name);
  return nameWithoutTitle ? `${title} ${nameWithoutTitle}` : name;
}

export function resolvePractitionerName(
  name: string,
  country: HospitalCountry | null | undefined,
  sources: readonly PractitionerNameSource[] | undefined,
  defaultToDoctor = false
): string {
  if (!sources) {
    return name;
  }

  const normalizedName = comparableName(name);
  const matches = sources.filter(
    (source) => comparableName(source.name) === normalizedName
  );

  if (matches.length === 0) {
    return formatPractitionerName(name, country, undefined, defaultToDoctor);
  }

  const allMatchesAreMd = matches.every(
    (source) => source.medicalCredentials === 'MD'
  );
  return formatPractitionerName(
    name,
    country,
    allMatchesAreMd ? 'MD' : undefined
  );
}
